(function () {
  'use strict';

  /**
   * Values For Life — Shopify product-page personalization handoff.
   * Captures Django approval redirect params, stores them briefly, cleans the URL,
   * and populates line-item property fields for checkout/webhooks.
   *
   * Contract: docs/SHOPIFY_INTEGRATION.md
   */
  const STORAGE_KEY = 'vfl_personalization_handoff';
  const MAX_AGE_MS = 30 * 60 * 1000;
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const TOKEN_RE = /^[A-Za-z0-9_-]{20,128}$/;

  const SENSITIVE_URL_PARAMS = [
    '_tracking_token',
    '_shopify_link_token',
    '_job',
    '_p13n_token',
    'child_name',
    'child_age',
    'job',
    'token',
    'book_id',
    'shop_id',
    '_print_format',
  ];

  const PROPERTY_FIELDS = [
    { stateKey: 'child_name', selector: '[data-vfl-property="child_name"]' },
    { stateKey: '_job', selector: '[data-vfl-property="_job"]' },
    { stateKey: '_p13n_token', selector: '[data-vfl-property="_p13n_token"]' },
    { stateKey: '_tracking_token', selector: '[data-vfl-property="_tracking_token"]' },
    { stateKey: '_shopify_link_token', selector: '[data-vfl-property="_shopify_link_token"]' },
  ];

  /** @type {Record<string, string> | null} */
  let memoryHandoff = null;
  let populateScheduled = false;
  let observer = null;

  function readStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.capturedAt || Date.now() - parsed.capturedAt > MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (_err) {
      return null;
    }
  }

  function writeStorage(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_err) {
      // sessionStorage may be unavailable; memory fallback only.
    }
  }

  function clearStorage() {
    memoryHandoff = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_err) {
      // ignore
    }
  }

  function getHandoffState() {
    if (memoryHandoff) return memoryHandoff;
    const stored = readStorage();
    if (stored) memoryHandoff = stored;
    return memoryHandoff;
  }

  function resolveJobId(params) {
    return (
      (params.get('_job') || '').trim() ||
      (params.get('job') || '').trim() ||
      (params.get('_p13n_token') || '').trim() ||
      (params.get('token') || '').trim() ||
      ''
    );
  }

  function isValidToken(value) {
    return typeof value === 'string' && TOKEN_RE.test(value);
  }

  function buildStateFromParams(params) {
    const jobId = resolveJobId(params);
    const trackingToken = (params.get('_tracking_token') || '').trim();
    const shopifyLinkToken = (params.get('_shopify_link_token') || '').trim();
    const childName = (params.get('child_name') || '').trim();
    const childAge = (params.get('child_age') || '').trim();
    const p13nToken = (params.get('_p13n_token') || '').trim() || jobId;

    if (!jobId && !trackingToken && !shopifyLinkToken && !childName) {
      return null;
    }

    if (jobId && !UUID_RE.test(jobId)) {
      return null;
    }
    if (trackingToken && !isValidToken(trackingToken)) {
      return null;
    }
    if (shopifyLinkToken && !isValidToken(shopifyLinkToken)) {
      return null;
    }

    return {
      capturedAt: Date.now(),
      child_name: childName,
      child_age: childAge,
      _job: jobId,
      _p13n_token: p13nToken,
      _tracking_token: trackingToken,
      _shopify_link_token: shopifyLinkToken,
    };
  }

  function mergeHandoffState(incoming) {
    if (!incoming) return getHandoffState();

    const existing = getHandoffState();
    if (!existing) {
      memoryHandoff = incoming;
      writeStorage(incoming);
      return memoryHandoff;
    }

    const merged = { ...existing, capturedAt: Date.now() };
    for (const key of [
      'child_name',
      'child_age',
      '_job',
      '_p13n_token',
      '_tracking_token',
      '_shopify_link_token',
    ]) {
      if (incoming[key]) merged[key] = incoming[key];
    }

    memoryHandoff = merged;
    writeStorage(merged);
    return memoryHandoff;
  }

  function captureFromUrlIfPresent() {
    const params = new URLSearchParams(window.location.search);
    const incoming = buildStateFromParams(params);
    if (!incoming) return false;

    mergeHandoffState(incoming);
    cleanPersonalizationFromUrl();
    return true;
  }

  function cleanPersonalizationFromUrl() {
    const url = new URL(window.location.href);
    let changed = false;

    SENSITIVE_URL_PARAMS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (!changed) return;
    window.history.replaceState({}, document.title, url.toString());
  }

  function populatePropertyFields(state) {
    if (!state) return;

    PROPERTY_FIELDS.forEach(({ stateKey, selector }) => {
      const value = state[stateKey];
      if (!value) return;
      document.querySelectorAll(selector).forEach((input) => {
        if (input instanceof HTMLInputElement && input.value !== value) {
          input.value = value;
        }
      });
    });
  }

  function updateChildDisplay(state) {
    const container = document.getElementById('vfl-child-handoff');
    const nameLine = document.getElementById('vfl-child-name-line');
    const ageLine = document.getElementById('vfl-child-age-line');
    if (!container || !nameLine) return;

    const childName = (state && state.child_name) || '';
    const childAge = (state && state.child_age) || '';

    if (!childName && !childAge) {
      container.hidden = true;
      return;
    }

    nameLine.textContent = '';
    ageLine.textContent = '';

    if (childName) {
      nameLine.appendChild(document.createTextNode('Creating this story for '));
      const strong = document.createElement('strong');
      strong.textContent = childName;
      nameLine.appendChild(strong);
    }

    if (childAge) {
      ageLine.appendChild(document.createTextNode('Child age: '));
      const strong = document.createElement('strong');
      strong.textContent = childAge;
      ageLine.appendChild(strong);
      ageLine.hidden = false;
    } else {
      ageLine.hidden = true;
    }

    container.hidden = false;
  }

  function personalizationRequired() {
    const form = document.querySelector('form[data-vfl-personalization-required="true"]');
    return Boolean(form);
  }

  function hasRequiredSecureHandoff(state) {
    if (!state) return false;
    return (
      UUID_RE.test(state._job || '') &&
      isValidToken(state._tracking_token || '') &&
      isValidToken(state._shopify_link_token || '')
    );
  }

  function setCheckoutEnabled(enabled) {
    const selectors = [
      '.accelerated-checkout-block',
      '.add-to-cart-button',
      'shopify-accelerated-checkout',
      'sticky-add-to-cart',
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (enabled) {
          el.removeAttribute('hidden');
          el.removeAttribute('aria-disabled');
          if ('disabled' in el) el.disabled = false;
          el.style.pointerEvents = '';
          el.style.opacity = '';
        } else {
          el.setAttribute('hidden', 'hidden');
          el.setAttribute('aria-disabled', 'true');
          if ('disabled' in el) el.disabled = true;
        }
      });
    });
  }

  function updateCheckoutGate(state) {
    const gate = document.getElementById('vfl-personalization-gate-message');
    const required = personalizationRequired();
    const ready = !required || hasRequiredSecureHandoff(state);

    if (gate) {
      gate.hidden = ready;
    }

    setCheckoutEnabled(ready);
  }

  function applyHandoff() {
    const state = getHandoffState();
    populatePropertyFields(state);
    updateChildDisplay(state);
    updateCheckoutGate(state);
  }

  function scheduleApplyHandoff() {
    if (populateScheduled) return;
    populateScheduled = true;
    requestAnimationFrame(() => {
      populateScheduled = false;
      applyHandoff();
    });
  }

  function observeProductSection() {
    const productRoot =
      document.querySelector('product-form-component') ||
      document.querySelector('[data-type="add-to-cart-form"]')?.closest('section') ||
      document.getElementById('MainContent');

    if (!productRoot || observer) return;

    observer = new MutationObserver(() => {
      scheduleApplyHandoff();
    });

    observer.observe(productRoot, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    captureFromUrlIfPresent();

    const stored = readStorage();
    if (stored) {
      memoryHandoff = stored;
      cleanPersonalizationFromUrl();
    }

    applyHandoff();
    observeProductSection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', () => {
    scheduleApplyHandoff();
  });
})();
